import { types, flow, getRoot, getParent } from 'mobx-state-tree';
import moment from 'moment';
import { when } from 'mobx';
import {
  Reload,
  Current,
  Transaction,
  AccountsBalance,
  Invoice,
  DepositsPagination,
} from '../../models/SettingsStore/Balance';
import { ReportsFilters } from '../common';
import {
  getPayoneerUrlForBalanceService,
  getEnvUrl,
  getRedirectStripeUrl,
} from '../../utils/getEnvUrl';
import downloadFile from '../../utils/downloadFile';

const URL = 'balance-service';

const Balance = types
  .model('Balance', {
    autoReload: types.optional(Reload, {}),
    current: types.optional(Current, {}),
    deposits: types.optional(types.array(Transaction), []),
    accountsBalance: types.optional(types.array(AccountsBalance), []),
    depositsPagination: types.optional(DepositsPagination, {}),
    invoices: types.optional(types.array(Invoice), []),
    reportsTransactions: types.optional(types.array(Transaction), []),
    reportsFilters: types.optional(ReportsFilters, {}),
    paypalSessionToken: types.optional(types.string, ''),
    paypalPaymentOptions: types.optional(types.array(types.string), []),
    payoneerPaymentOptions: types.optional(types.array(types.string), []),
    stripePaymentOptions: types.optional(types.array(types.frozen), []),
    paymentEnabledMethods: types.optional(types.frozen, {}),
    paypalEligible: types.optional(types.number, 0),
    paypalConnected: false,
    payoneerConnected: false,
    stripeConnected: false,
    payoneerSubmitResponse: types.optional(types.frozen, {}),
    stripeSubmitResponse: types.optional(types.frozen, {}),
    lastBalanceLoad: types.frozen,
  })
  .views(self => ({
    get transport5007() {
      return getRoot(self).uiStore.transport5007;
    },
    get showToast() {
      return getParent(self, 2).toastsStore.showToast;
    },
    get enums() {
      return getParent(self, 2).enumsStore.getEnumsList;
    },
    get reportsData() {
      const jsonData = self.reportsTransactions.map(
        ({ created, debit, credit, extra, external_id, type, balance_after, app, id }) => ({
          Date: moment(created).format('MMMM D YYYY HH:mm') || '',
          'Transaction Type': type || '',
          Debit: debit,
          Credit: credit,
          'Transaction Id': extra?.order_id ?? extra?.sub_reason ?? external_id ?? '',
          'Balance After': Math.round(balance_after * 100) / 100,
          App: app,
          'Balance Txn': id,
          'External Order Id': extra?.dest_order_id ?? '',
        }),
      );
      let csv = (jsonData[0] ? Object.keys(jsonData[0]) : []).join(',');
      csv += '\n';
      jsonData.forEach(elem => {
        csv += Object.values(elem).join(',');
        csv += '\n';
      });
      return csv;
    },
  }))
  .actions(self => ({
    fetchPaypalSessionToken: flow(function* fetchPaypalSessionToken() {
      try {
        const { data } = yield self.transport5007.fetch(
          `${URL}/payments/paypal/sessionToken`,
          {},
          {},
        );
        self.paypalSessionToken = data.data.token;
        return true;
      } catch (err) {
        console.error(err);
        return false;
      }
    }),
    // fetchClientConnectionStatus: flow(function* fetchClientConnectionStatus() {
    //   try {
    //     const {
    //       data: { data },
    //     } = yield self.transport5007.fetch(`${URL}/payments/paypal/clientToken`, {}, {});
    //     self.setPaypalConnected(data.hasToken);
    //   } catch (err) {
    //     console.error(err);
    //   }
    // }),
    fetchPaypalEligible: flow(function* fetchPaypalEligible() {
      try {
        const {
          data: { data },
        } = yield self.transport5007.fetch(`${URL}/payments/paypal/isEligible`, {}, {});
        if(data.is_eligible === true){
          self.paypalEligible = data.balance;
        }
        else{
          self.paypalEligible = 0;
        }
      } catch (err) {
        console.error(err);
      }
    }),
    fetchPaymentEnabledMethods: flow(function* fetchPaymentEnabledMethods() {
      try {
        const {
          data: { data },
        } = yield self.transport5007.fetch(`${URL}/payments/enabledPayments`, {}, {});
        self.paymentEnabledMethods = data;
      } catch (err) {
        console.error(err);
      }
    }),
    fetchPaypalPaymentOptions: flow(function* fetchPaypalPaymentOptions() {
      try {
        const {
          data: { data },
        } = yield self.transport5007.fetch(`${URL}/payments/paypal/paymentOptions`, {}, {});
        self.setPaypalConnected(data && data.length);
        self.paypalPaymentOptions = data;
      } catch (err) {
        console.error(err);
      }
    }),
    fetchPayoneerPaymentOptions: flow(function* fetchPayoneerPaymentOptions() {
      try {
        const {
          data: { data },
        } = yield self.transport5007.fetch(`${URL}/payments/payoneer/paymentOptions`, {}, {});
        self.setPayoneerConnected(data && data.length);
        self.payoneerPaymentOptions = data;
        // self.payoneerPaymentOptions = data.map(dat=>dat.email);
      } catch (err) {
        console.error(err);
      }
    }),
    fetchAccountsBalance: flow(function* fetchAccountsBalance(currency) {
      try {
        const {
          data: { data },
        } = yield self.transport5007.fetch(`${URL}/payments/payoneer/getBalances/${currency}`, {}, {});
        self.accountsBalance=data;
      } catch (err) {
        console.error(err);
      }
    }),
    fetchStripePaymentOptions: flow(function* fetchStripePaymentOptions() {
      try {
        const {
          data: { data },
        } = yield self.transport5007.fetch(`${URL}/payments/stripe/paymentOptions`, {}, {});
        self.setStripeConnected(data && data.length);
        self.stripePaymentOptions = data;
      } catch (err) {
        console.error(err);
      }
    }),
    sendAuthCode: flow(function* sendAuthCode(authCode) {
      return yield self.transport5007.post(
        `${URL}/payments/payoneer/auth`,
        {
          authCode,
          redirectUrl: `${getEnvUrl()}/close`,
        },
        {},
      );
    }),
    sendCodeAfterConnection(code) {
      return new Promise((resolve, reject) => {
        when(
          () => getParent(self).markets.defaultDestination.id,
          () =>
            self
              .sendAuthCode(code)
              .then(data => resolve(data))
              .catch(err => reject(err)),
        );
      });
    },
    setPayoneerSubmitResponse(v) {
      self.payoneerSubmitResponse = v;
    },
    registerPayoneer: flow(function* registerPayoneer() {
      try {
        self.payoneerSubmitResponse = {};
        const authWindow = window.open(
          getPayoneerUrlForBalanceService(),
          'balance',
          'height=750, width=750',
        );
        window.addEventListener(
          'message',
          function handleWindowMessage(e) {
            if (!e.data.type || e.data.type !== 'OAuthAccessCode') {
              return;
            }
            authWindow.close();
            window.removeEventListener('message', handleWindowMessage, false);
            if (e.data.payload) {
              self.setPayoneerSubmitResponse(e.data.payload);
            }
          },
          false,
        );
        yield when(() => Object.keys(self.payoneerSubmitResponse).length !== 0);
        return self.payoneerSubmitResponse.isOk;
      } catch (err) {
        console.error(err);
        return false;
      }
    }),
    // eslint-disable-next-line consistent-return
    registerStripe: flow(function* registerStripe() {
      try {
        const { data } = yield self.transport5007.post('stripe-service/sessions', {
          successUrl: getRedirectStripeUrl().success,
          cancelUrl: getRedirectStripeUrl().cancel,
          email: self.userEmail,
        });
        const y = window.top.outerHeight / 2 + window.top.screenY - 750 / 2;
        const x = window.top.outerWidth / 2 + window.top.screenX - 450 / 2;

        const stripeAuthWindow = window.open(
          data.url,
          'balance',
          `toolbar=no, location=no, directories=no, status=no, menubar=no, scrollbars=no, resizable=no, copyhistory=no, width=450, height=750, top=${y}, left=${x}`,
        );

        window.addEventListener(
          'message',
          function handleWindowMessage(e) {
            if (!e.data.type || e.data.type !== 'OAuthAccessCode') {
              return;
            }
            window.removeEventListener('message', handleWindowMessage, false);
            if (e.data.client === '/successStripe') {
              self.setStripeSubmitResponse(e.data);
              stripeAuthWindow.close();
            } else {
              self.showToast({
                type: self.enums.TOAST_TYPES.WARNING,
                message: 'An error has occurred while adding the account. Please try again.',
              });
              stripeAuthWindow.close();
            }
          },
          false,
        );
        yield when(() => Object.keys(self.stripeSubmitResponse).length !== 0);
        return true;
      } catch (err) {
        console.error(err);
        self.showToast({
          type: self.enums.TOAST_TYPES.WARNING,
          message: 'An error has occurred while adding the account. Please try again.',
        });
        return false;
      }
    }),
    // eslint-disable-next-line consistent-return
    setStripeSubmitResponse: flow(function* setStripeSubmitResponse(value) {
      try {
        yield self.transport5007.post(`${URL}/payments/stripe/connect`, {
          session: value.payload,
        });
        self.showToast({
          type: self.enums.TOAST_TYPES.INFO,
          message: 'You have successfully connected your credit card.',
        });
        self.stripeSubmitResponse = value.payload;
      } catch (err) {
        console.error(err);
        self.showToast({
          type: self.enums.TOAST_TYPES.WARNING,
          message: 'An error has occurred while adding your credit card. Please try again.',
        });
        return false;
      }
    }),
    fetchBalanceData: flow(function* fetchBalanceData() {
      // @todo will have to send currency if endpoint changed
      const currencyId = getParent(self).userProfile.defaultCurrencyId;
      try {
        const { data } = yield self.transport5007.fetch(`${URL}/balance`, {}, {});
        self.autoReload = Reload.create({ [currencyId]: data.data.auto_reload });
        self.current = data.data.current;
      } catch (err) {
        console.error(err);
      }
    }),
    fetchTransactions: flow(function* fetchTransactions(forReports = false, addResults = false) {
      try {
        if (forReports) {
          const from = moment(
            `${self.reportsFilters.fromDate} 00:00 +0000`,
            'YYYY-MM-DD HH:mm Z',
          ).format('X');
          const to = moment(
            `${self.reportsFilters.toDate} 23:59 +0000`,
            'YYYY-MM-DD HH:mm Z',
          ).format('X');
          const page_size = 5000;
          const { data } = yield self.transport5007.fetch(
            `${URL}/transactions?from=${from}&to=${to}&page_size=${page_size}&status=approved&type=${
              self.reportsFilters.transactionType
            }`,
            {},
            {},
            {},
          );
          self.reportsTransactions = data.data;
        } else {
          const { start, limit } = self.depositsPagination;
          const [firstResponse, secondResponse] = yield Promise.all([
            self.transport5007.fetch(
              `${URL}/transactions?type=deposit&start=${start}&length=${limit}`,
              {},
              {},
            ),
            self.transport5007.fetch(
              `${URL}/transactions?type=deposit_manual&start=${start}&length=${limit}`,
              {},
              {},
            ),
          ]);
          const {
            data: { data: depositData, recordsFiltered },
          } = firstResponse;
          const {
            data: { data: depositManualData, recordsFiltered: recordsFilteredManual },
          } = secondResponse;
          const combinedData = [...depositData, ...depositManualData];
          const combinedRecords = recordsFiltered + recordsFilteredManual;
          self.deposits = addResults ? [...self.deposits, ...combinedData] : combinedData;
          self.depositsPagination.setTotal(combinedRecords ?? 0);
        }
        return true;
      } catch (err) {
        console.error(err);
        return false;
      }
    }),
    fetchFirstTransactionsPage: flow(function* fetchFirstTransactionsPage() {
      self.depositsPagination.setStart(0);
      self.depositsPagination.setLimit(3);
      yield self.fetchTransactions();
    }),
    fetchNextTransactionsPage: flow(function* fetchNextTransactionsPage() {
      self.depositsPagination.setStart(self.deposits.length);
      self.depositsPagination.setLimit(10);
      yield self.fetchTransactions(false, true);
    }),
    fetchInvoices: flow(function* fetchInvoices() {
      try {
        const { data } = yield self.transport5007.fetch('order/v1/balance/invoices');
        self.invoices = data;
      } catch (err) {
        console.log(err);
      }
    }),
    getInvoiceFile: flow(function* getInvoiceFile(id, name) {
      try {
        const { data } = yield self.transport5007.fetch(
          `order/v1/balance/invoice/${id}/file`,
          {},
          {},
          'blob',
        );
        downloadFile(data, name, 'application/pdf');
      } catch (err) {
        console.log(err);
      }
    }),
    saveAutoReload: flow(function* saveAutoReload() {
      // @todo change request body
      const currencyId = getParent(self).userProfile.defaultCurrencyId;
      try {
        yield self.transport5007.post(`${URL}/balance/autoReload`, self.autoReload[currencyId]);
        return true;
      } catch (err) {
        console.error(err);
        return false;
      }
    }),
    deletePaymentOption: flow(function* deletePaymentOption(name) {
      const currencyId = getParent(self).userProfile.defaultCurrencyId;
      try {
        yield self.transport5007.delete(
          `${URL}/payments/paypal/paymentOptions/${name}`,
          {},
          self.autoReload[currencyId],
        );
        yield self.fetchPaypalEligible();
        yield self.fetchPaypalPaymentOptions();
        return true;
      } catch (err) {
        console.error(err);
        return false;
      }
    }),
    deletePayoneerPaymentOption: flow(function* deletePayoneerPaymentOption(name) {
      const currencyId = getParent(self).userProfile.defaultCurrencyId;
      try {
        yield self.transport5007.delete(
          `${URL}/payments/payoneer/paymentOptions/${name}`,
          {},
          self.autoReload[currencyId],
        );
        yield self.fetchPayoneerPaymentOptions();
        return true;
      } catch (err) {
        console.error(err);
        return false;
      }
    }),
    deleteStripePaymentOption: flow(function* deleteStripePaymentOption(pm_id) {
      const currencyId = getParent(self).userProfile.defaultCurrencyId;
      try {
        yield self.transport5007.delete(
          `${URL}/payments/stripe/paymentOptions/${pm_id}`,
          {},
          self.autoReload[currencyId],
        );
        yield self.fetchStripePaymentOptions();
        return true;
      } catch (err) {
        console.error(err);
        return false;
      }
    }),
    sendPaypalConnectEvent: flow(function* sendPaypalConnectEvent(name, paymentMethodNonce) {
      try {
        yield self.transport5007.post(`account/v1/paypal-accounts/for-balance`, {
          name,
          paymentMethodNonce,
        });
      } catch (err) {
        console.error(err);
      }
    }),
    depositCurrency: flow(function* depositCurrency(amount, commission, currency, nonce, pp_user) {
      try {
        let params = {
          amount: amount + commission,
          currency,
          app: 'B2B',
        };
        if (nonce && !self.paypalConnected) {
          // on first deposit
          params = {
            ...params,
            nonce,
          };
          const data = yield self.transport5007.post(`${URL}/payments/paypal/checkout`, params);
          if (data.data.status === 'error') {
            console.log(data.data.message);
            return false;
          }
          self.sendPaypalConnectEvent(pp_user, nonce);
          self.fetchPaypalEligible();
          self.fetchPaypalPaymentOptions();
        } else {
          // on later deposits
          params = {
            ...params,
            useToken: true,
            pp_user,
          };
          const data = yield self.transport5007.post(`${URL}/payments/paypal/checkout`, params);
          if (data.data.status === 'error') {
            console.log(data.data.message);
            return false;
          }
        }
        yield Promise.allSettled([
          getRoot(self).uiStore.dashboardStore.fetchMiniDashboard(),
          self.fetchBalanceData(),
        ]);
        self.lastBalanceLoad = { amount, currency: params.currency };
        return true;
      } catch (err) {
        console.error(err);
        return false;
      }
    }),
    depositCurrencyPayoneer: flow(function* depositCurrencyPayoneer(
      amount,
      commission,
      currency,
      pp_user,
    ) {
      try {
        let params = {
          amount: amount + commission,
          currency,
          app: 'B2B',
        };
        if (!self.payoneerConnected) {
          // on first deposit
          const resp = yield self.registerPayoneer();

          if (!resp) {
            return resp;
          }
          yield self.fetchPayoneerPaymentOptions();
          params = {
            ...params,
            useToken: true,
            pp_user: self.payoneerPaymentOptions[self.payoneerPaymentOptions.length - 1],
          };
        } else {
          params = {
            ...params,
            useToken: true,
            pp_user,
          };
        }
        // on later deposits

        const { data } = yield self.transport5007.post(`${URL}/payments/payoneer/checkout`, params);
        if (data.status === 'error') {
          return false;
        }
        yield Promise.allSettled([
          getRoot(self).uiStore.dashboardStore.fetchMiniDashboard(),
          self.fetchBalanceData(),
        ]);
        self.lastBalanceLoad = { amount, currency: params.currency };
        return true;
      } catch (err) {
        console.error(err);
        return false;
      }
    }),
    depositCurrencyStripe: flow(function* depositCurrencyStripe(
      amount,
      commission,
      currency,
      pp_user,
    ) {
      try {
        let params = {
          amount: amount + commission,
          currency,
          app: 'B2B',
        };
        if (!self.stripeConnected) {
          // on first deposit
          const resp = yield self.registerStripe();

          if (!resp) {
            return resp;
          }
          yield self.fetchStripePaymentOptions();
          params = {
            ...params,
            pp_user:
              self.stripePaymentOptions[0].cards[self.stripePaymentOptions[0].cards.length - 1]
                .pm_id,
          };
        } else {
          params = {
            ...params,
            pp_user,
          };
        }
        // on later deposits

        const { data } = yield self.transport5007.post(`${URL}/payments/stripe/checkout`, params);
        if (data.status === 'error') {
          return false;
        }
        yield Promise.allSettled([
          getRoot(self).uiStore.dashboardStore.fetchMiniDashboard(),
          self.fetchBalanceData(),
        ]);
        self.lastBalanceLoad = { amount, currency: params.currency };
        return true;
      } catch (err) {
        console.error(err);
        return false;
      }
    }),
    initBalance: flow(function* initStore() {
      yield Promise.allSettled([
        self.fetchInvoices(),
        self.fetchBalanceData(),
        self.fetchFirstTransactionsPage(),
        // self.fetchClientConnectionStatus(),
        self.fetchPaypalEligible(),
        self.fetchPaymentEnabledMethods(),
        self.fetchPaypalPaymentOptions(),
        self.fetchPayoneerPaymentOptions(),
        self.fetchStripePaymentOptions(),
        self.fetchPaypalSessionToken(),
      ]);
    }),
    setPaypalConnected(value) {
      self.paypalConnected = !!value;
    },
    setPayoneerConnected(value) {
      self.payoneerConnected = !!value;
    },
    setStripeConnected(value) {
      self.stripeConnected = !!value;
    },
  }));

export default Balance;
